import React from "react";
import { GoogleEventDetailsModal } from "@/components/Calendar/schedule/GoogleEventDetailsModal";
import { BookingCard } from "./BookingCard";
import { BlockOutCard } from "./BlockOutCard";
import { TaskCard } from "./TaskCard";
import {
  type Booking,
  type BlockOut,
  type Task,
} from "@/components/Calendar/scheduleData";

type GoogleCalendarEvent = {
  id: string;
  summary: string;
  description?: string;
  date: string;
  time: string;
  duration: number;
  htmlLink?: string;
};

interface CalendarDayCellProps {
  dayGoogleEvents?: GoogleCalendarEvent[];
  day: number;
  dateString: string;
  dayBookings: Booking[];
  dayBlockOuts: BlockOut[];
  dayTasks?: Task[];
  isToday: boolean;
  isNonWorking?: boolean;
  serviceColors: Record<string, string>;
  serviceNameMap?: Record<string, string>;
  serviceColorMap?: Record<string, string>;
  onCreateBooking: (bookingData: any) => void;
  onEditBooking: (bookingId: string, bookingData: any) => void;
  onDeleteBooking: (bookingId: string) => void;
  onStartConsultation?: (booking: Booking) => void;
  onEditBlockOut?: (blockOutId: string, blockOut: BlockOut) => void;
  onEditTask?: (taskId: string, task: Task) => void;
  onViewDay: (dateString: string) => void;
  onCreateBookingForDate: (dateString: string) => void;
  dayGoogleEvents?: GoogleCalendarEvent[];
  pharmacists: string[];
  rooms: string[];
  isDarkMode?: boolean;
  onBookingDrop?: (booking: Booking, newDate: string, newTime?: string) => void;
  onDragStart?: (booking: Booking) => void;
  notifiedBookingId?: string | null;
  notificationType?: "message" | "check";
}

export function CalendarDayCell({
  day,
  dateString,
  dayBookings,
  dayBlockOuts,
  dayTasks = [],
  isToday,
  isNonWorking = false,
  serviceColors,
  serviceNameMap = {},
  serviceColorMap = {},
  onCreateBooking,
  onEditBooking,
  onDeleteBooking,
  onStartConsultation,
  onEditBlockOut,
  onEditTask,
  onViewDay,
  onCreateBookingForDate,
  pharmacists,
  rooms,
  dayGoogleEvents = [],
  isDarkMode = false,
  onBookingDrop,
  onDragStart,
  notifiedBookingId,
  notificationType = "check"
}: CalendarDayCellProps) {
  const [isDragOver, setIsDragOver] = React.useState(false);
  const [selectedGoogleEvent, setSelectedGoogleEvent] =
    React.useState<any>(null);
  const [isGoogleEventModalOpen, setIsGoogleEventModalOpen] =
    React.useState(false);

  const isPastDate = (dateStr: string) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const date = new Date(`${dateStr}T00:00:00`);
    return date.getTime() < today.getTime();
  };

  const isBlocked = isPastDate(dateString) || isNonWorking;

  const handleCellClick = (e: React.MouseEvent) => {
    if (isBlocked) return;

    // Only show create modal if clicking on empty space
    const target = e.target as HTMLElement;
    const isBookingCard = target.closest("[data-booking-card]");
    const isBlockOutCard = target.closest("[data-blockout-card]");
    const isTaskCard = target.closest("[data-task-card]");
    const isMoreButton = target.closest("[data-more-button]");
    const isGoogleEventCard = target.closest("[data-google-event-card]");

    if (
      !isBookingCard &&
      !isBlockOutCard &&
      !isTaskCard &&
      !isMoreButton &&
      !isGoogleEventCard
    ) {
      onCreateBookingForDate(dateString);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    // Drag and drop disabled
    return;
    /*
    if (isBlocked) return;
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
    e.dataTransfer.dropEffect = "move";
    */
  };

  const handleDragLeave = (e: React.DragEvent) => {
    // Drag and drop disabled
    return;
    /*
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
    */
  };

  const handleDrop = (e: React.DragEvent) => {
    // Drag and drop disabled
    return;
    /*
    if (isBlocked) return;
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);

    const bookingData = e.dataTransfer.getData("application/json");
    if (bookingData && onBookingDrop) {
      const booking = JSON.parse(bookingData) as Booking;
      // Trigger drop for any date change
      onBookingDrop(booking, dateString);
    }
    */
  };

  return (
    <>
      <div
        className={`h-36 p-2 border-r border-b transition-all duration-200 flex flex-col overflow-hidden ${
          isBlocked
            ? isDarkMode
              ? "bg-gray-800 cursor-not-allowed pointer-events-none border-gray-700"
              : "bg-gray-200 cursor-not-allowed pointer-events-none border-gray-300"
            : isDragOver
              ? isDarkMode
                ? "bg-blue-800 border-blue-500 border-2 ring-2 ring-blue-500 cursor-pointer"
                : "bg-blue-100 border-blue-400 border-2 ring-2 ring-blue-400 cursor-pointer"
              : isDarkMode
                ? `${isToday ? "bg-blue-900 hover:bg-blue-800" : "bg-gray-800 hover:bg-gray-700"} border-gray-600 cursor-pointer`
                : `${isToday ? "bg-blue-50 hover:bg-blue-100" : "bg-white hover:bg-gray-50"} border-gray-200 cursor-pointer`
        }`}
        style={
          isBlocked
            ? {
                backgroundImage: `repeating-linear-gradient(
                45deg,
                transparent,
                transparent 6px,
                ${isDarkMode ? "rgba(0, 0, 0, 0.15)" : "rgba(0, 0, 0, 0.04)"} 6px,
                ${isDarkMode ? "rgba(0, 0, 0, 0.15)" : "rgba(0, 0, 0, 0.04)"} 12px
              )`,
              }
            : undefined
        }
        title={
          isPastDate(dateString)
            ? `Cannot create booking on past dates`
            : isNonWorking
              ? `Not available - ${dateString}`
              : `Click to add booking for ${dateString}`
        }
        onClick={handleCellClick}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        <div
          className={`text-sm font-medium mb-1 flex-shrink-0 transition-colors duration-300 ${
            isToday
              ? "text-blue-600"
              : isDarkMode
                ? "text-white"
                : "text-gray-900"
          }`}
        >
          {day}
        </div>

        <div className="flex-1 min-h-0 flex flex-col overflow-hidden">
          {/* Calculate how many items to show based on whether we need a +more button */}
          {(() => {
            const totalItems =
              dayBookings.length +
              dayBlockOuts.length +
              dayTasks.length +
              dayGoogleEvents.length;
            const hasMoreItems =
              dayBookings.length > 2 ||
              dayBlockOuts.length > 1 ||
              dayTasks.length > 1 ||
              dayGoogleEvents.length > 1;
            const maxBookingsToShow = hasMoreItems ? 1 : 2; // Show only 1 booking if we need space for +more
            const maxBlockOutsToShow = hasMoreItems ? 0 : 1; // Don't show block-outs if we need space for +more
            const maxTasksToShow = hasMoreItems ? 0 : 1; // Don't show tasks if we need space for +more
            const maxGoogleEventsToShow = hasMoreItems ? 0 : 1;

            return (
              <>
                <div className="space-y-1 flex-1 min-h-0 overflow-hidden">
                  {dayBookings.slice(0, maxBookingsToShow).map((booking) => (
                    <div
                      key={booking.id}
                      data-booking-card
                      className="flex-shrink-0"
                    >
                      <BookingCard
                        booking={booking}
                        serviceColors={serviceColors}
                        serviceNameMap={serviceNameMap}
                        serviceColorMap={serviceColorMap}
                        onEdit={onEditBooking}
                        onDelete={onDeleteBooking}
                        onStartConsultation={onStartConsultation}
                        pharmacists={pharmacists}
                        rooms={rooms}
                        size="medium"
                        isDarkMode={isDarkMode}
                        onDragStart={onDragStart}
                        showNotification={notifiedBookingId === booking.id}
                        notificationType={notificationType}
                      />
                    </div>
                  ))}

                  {dayGoogleEvents
                    .slice(0, maxGoogleEventsToShow)
                    .map((event) => (
                      <div
                        key={event.id}
                        data-google-event-card
                        className="flex-shrink-0"
                      >
                        <div
                          onClick={() => {
                            setSelectedGoogleEvent(event);
                            setIsGoogleEventModalOpen(true);
                          }}
                          className={`rounded-md border p-2 text-[11px] font-medium overflow-hidden truncate cursor-pointer transition-all hover:shadow-md ${
                            isDarkMode
                              ? "bg-blue-950/80 border-blue-700 text-white hover:bg-blue-900/90"
                              : "bg-blue-50 border-blue-200 text-blue-900 hover:bg-blue-100"
                          }`}
                        >
                          <div className="flex items-start justify-between gap-2">
                            <div className="flex items-center gap-2 min-w-0">
                              <span
                                className={`inline-flex items-center justify-center w-7 h-7 rounded-full text-sm font-bold flex-shrink-0 ${
                                  isDarkMode
                                    ? "bg-red-400 text-black"
                                    : "bg-red-600 text-white"
                                }`}
                                title="Google event"
                              >
                                G
                              </span>
                              <div className="truncate min-w-0">
                                {event.summary}
                              </div>
                            </div>
                          </div>

                          <div className="mt-1 text-[10px]">
                            {event.time} • {event.duration}m
                          </div>
                        </div>
                      </div>
                    ))}

                  {/* Block-out periods */}
                  {dayBlockOuts.slice(0, maxBlockOutsToShow).map((blockOut) => (
                    <div
                      key={blockOut.id}
                      data-blockout-card
                      className="flex-shrink-0"
                    >
                      <BlockOutCard
                        blockOut={blockOut}
                        size="medium"
                        isDarkMode={isDarkMode}
                        onEdit={onEditBlockOut}
                      />
                    </div>
                  ))}

                  {/* Tasks */}
                  {dayTasks.slice(0, maxTasksToShow).map((task) => (
                    <div key={task.id} data-task-card className="flex-shrink-0">
                      <TaskCard
                        task={task}
                        size="medium"
                        isDarkMode={isDarkMode}
                        onEdit={onEditTask}
                      />
                    </div>
                  ))}
                </div>

                {hasMoreItems && (
                  <div
                    data-more-button
                    className={`text-xs text-center cursor-pointer hover:opacity-80 pointer-events-auto transition-colors duration-300 flex-shrink-0 py-1 mt-1 ${
                      isDarkMode
                        ? "text-gray-400 hover:text-gray-300"
                        : "text-gray-500 hover:text-gray-600"
                    }`}
                    onClick={(e) => {
                      e.stopPropagation();
                      onViewDay(dateString);
                    }}
                  >
                    +
                    {Math.max(dayBookings.length - maxBookingsToShow, 0) +
                      Math.max(
                        dayGoogleEvents.length - maxGoogleEventsToShow,
                        0,
                      ) +
                      Math.max(dayBlockOuts.length - maxBlockOutsToShow, 0) +
                      Math.max(dayTasks.length - maxTasksToShow, 0)}{" "}
                    more
                  </div>
                )}
              </>
            );
          })()}
        </div>
      </div>
      <GoogleEventDetailsModal
        isDarkMode={isDarkMode}
        event={selectedGoogleEvent}
        isOpen={isGoogleEventModalOpen}
        onClose={() => setIsGoogleEventModalOpen(false)}
      />
    </>
  );
}
